import React, { useState, useEffect } from 'react';
import { X, Loader2, ShieldCheck } from 'lucide-react';
import { trustedSourceApi, TrustedSourceApplication } from '../services/postsApi';

interface Props {
  open: boolean;
  onClose: () => void;
}

const STATUS_COPY: Record<TrustedSourceApplication['status'], { color: string; text: string }> = {
  pending:   { color: 'text-amber-300',    text: 'Your application is under review.' },
  approved:  { color: 'text-emerald-300',  text: 'You are a Trusted Source. The Partner badge is live.' },
  rejected:  { color: 'text-red-300',      text: 'Your application was not approved at this time. You may reapply.' },
  withdrawn: { color: 'text-slate-400',    text: 'You withdrew this application. You can submit a new one.' },
};

const TrustedSourceApplyModal: React.FC<Props> = ({ open, onClose }) => {
  const [existing, setExisting] = useState<TrustedSourceApplication | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const [organization, setOrganization]       = useState('');
  const [contactEmail, setContactEmail]       = useState('');
  const [contactPhone, setContactPhone]       = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [whyTrusted, setWhyTrusted]           = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(null); setSubmitted(false);
    trustedSourceApi.myApplication()
      .then(r => setExisting(r.application))
      .catch(e => setError(e.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  }, [open]);

  const submit = async () => {
    setError(null);
    if (organization.trim().length < 2) return setError('Organization required.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactEmail.trim())) return setError('Valid contact email required.');
    if (roleDescription.trim().length < 10) return setError('Role description (10+ chars) required.');
    if (whyTrusted.trim().length < 30) return setError('Tell us in 30+ chars why we should trust you.');
    setLoading(true);
    try {
      await trustedSourceApi.apply({
        organization: organization.trim(),
        contactEmail: contactEmail.trim(),
        contactPhone: contactPhone.trim() || undefined,
        roleDescription: roleDescription.trim(),
        whyTrusted: whyTrusted.trim(),
      });
      setSubmitted(true);
      const r = await trustedSourceApi.myApplication();
      setExisting(r.application);
    } catch (e: any) {
      setError(e.message || 'Submit failed.');
    } finally {
      setLoading(false);
    }
  };

  const withdraw = async () => {
    if (!confirm('Withdraw your pending application?')) return;
    setLoading(true);
    try {
      await trustedSourceApi.withdraw();
      const r = await trustedSourceApi.myApplication();
      setExisting(r.application);
    } catch (e: any) {
      setError(e.message || 'Withdraw failed.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  const isPending = existing?.status === 'pending';
  const showForm  = !isPending && existing?.status !== 'approved';

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-amber-700/40 rounded-lg w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-slate-800">
          <h2 className="text-amber-200 font-bold tracking-wide flex items-center gap-2">
            <ShieldCheck size={18}/> TRUSTED SOURCE PROGRAM
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" aria-label="Close"><X size={18}/></button>
        </header>

        <div className="p-4 space-y-3 text-sm">
          <p className="text-slate-300 leading-snug">
            For partnered scanner-group admins, neighborhood watch leads, and local news contributors.
            Trusted Sources skip the standard auto-flag pipeline and get a Partner badge. We don't scrape any
            third-party platform — all outside content comes through Trusted Sources voluntarily cross-posting.
          </p>

          {existing && (
            <div className={`text-xs ${STATUS_COPY[existing.status].color} bg-slate-800/60 rounded p-2 border border-slate-700/60`}>
              <div className="font-bold uppercase tracking-wide">{existing.status}</div>
              <div>{STATUS_COPY[existing.status].text}</div>
              {existing.reviewNotes && <div className="mt-1 text-slate-300 italic">"{existing.reviewNotes}"</div>}
              {isPending && (
                <button onClick={withdraw} disabled={loading}
                  className="mt-2 text-[11px] underline text-slate-400 hover:text-slate-200">withdraw application</button>
              )}
            </div>
          )}

          {submitted && <div className="text-xs text-emerald-300 bg-emerald-900/30 border border-emerald-800 rounded px-2 py-1">Application submitted. We'll review it soon.</div>}

          {showForm && (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400">Organization / group</span>
                <input value={organization} onChange={e => setOrganization(e.target.value.slice(0, 200))}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400">Contact email</span>
                <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value.slice(0, 200))}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400">Contact phone (optional)</span>
                <input value={contactPhone} onChange={e => setContactPhone(e.target.value.slice(0, 40))}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400">Your role</span>
                <input value={roleDescription} onChange={e => setRoleDescription(e.target.value.slice(0, 1000))}
                  placeholder="e.g. Admin of Tucson Scanner Group on Facebook"
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400">Why should we trust you?</span>
                <textarea value={whyTrusted} onChange={e => setWhyTrusted(e.target.value.slice(0, 2000))} rows={4}
                  placeholder="Track record, references, links to prior work..."
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100 resize-y" />
              </label>

              {error && <div className="text-xs text-red-300 bg-red-900/30 border border-red-800 rounded px-2 py-1">{error}</div>}

              <div className="flex gap-2 pt-1">
                <button onClick={onClose} className="flex-1 px-3 py-2 rounded border border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</button>
                <button onClick={submit} disabled={loading}
                  className="flex-1 px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {loading && <Loader2 size={14} className="animate-spin"/>} Submit
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrustedSourceApplyModal;
